import { LGoButton, LGoDialog } from "@/ui/branded";
import { useOfferCancellationPreview } from "@/features/offers/hooks/usePartnerOffers";
import { formatPrice } from "@/lib/money";
import { czechPlural, reservationForms } from "@/lib/czech";
import {
  canDeleteOffer,
  cancellationReachedCustomers,
  isKeptOnlyForHistory,
} from "@/domain/offer/rules";
import type { OfferCancellation, PartnerOffer } from "@/domain/offer/types";

function customerMoneySentence(refundAmountMinor: number): string {
  if (refundAmountMinor === 0) return "Zákazníkům nic nestrhneme a jejich částku uvolníme.";
  return `Zákazníkům vrátíte ${formatPrice(refundAmountMinor)}, které jsme jim už strhli; kde se ještě nic nestrhlo, uvolníme částku.`;
}

function CancellationSummary({ tally }: { tally: OfferCancellation }) {
  if (canDeleteOffer(tally)) {
    return (
      <p className="text-sm text-muted-foreground">Nikdo nemá rezervaci — termín prostě smažeme.</p>
    );
  }
  if (isKeptOnlyForHistory(tally)) {
    return (
      <p className="text-sm text-foreground">
        Zrušení se už nikoho netýká. Termín v seznamu zůstane, protože k němu patří záznamy o
        dřívějších rezervacích nebo nedokončených platbách, ale přestane se prodávat.
      </p>
    );
  }
  const hasSoldReservations = tally.reservationCount > 0;
  return (
    <>
      {hasSoldReservations && (
        <p className="text-sm text-foreground">
          Zrušíte {tally.reservationCount} {czechPlural(tally.reservationCount, reservationForms)}.{" "}
          {customerMoneySentence(tally.refundAmountMinor)} O zrušení jim dáme vědět v aplikaci i
          e-mailem. Slevový termín se přestane prodávat.
        </p>
      )}
      {tally.keptShareCount > 0 && (
        <p className="text-sm text-foreground">
          Zákazníci už po bezplatné lhůtě zrušili {tally.keptShareCount}{" "}
          {czechPlural(tally.keptShareCount, reservationForms)}. Storno se jim nebude účtovat a o
          zrušení termínu jim dáme vědět.
          {!hasSoldReservations &&
            tally.refundAmountMinor > 0 &&
            ` ${customerMoneySentence(tally.refundAmountMinor)}`}
        </p>
      )}
      {tally.refundFailedAmountMinor > 0 && (
        <p className="text-sm text-foreground">
          Zákazníkům se nepodařilo vrátit {formatPrice(tally.refundFailedAmountMinor)}. Zrušením
          termínu se vrácení zkusí znovu.
        </p>
      )}
      {!hasSoldReservations && (
        <p className="text-sm text-foreground">Slevový termín se přestane prodávat.</p>
      )}
    </>
  );
}

interface CancelOfferDialogProps {
  offer: PartnerOffer | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isCancelling: boolean;
}

export function CancelOfferDialog({
  offer,
  onOpenChange,
  onConfirm,
  isCancelling,
}: CancelOfferDialogProps) {
  const preview = useOfferCancellationPreview(offer?.id ?? null);
  const tally = preview.data;

  return (
    <LGoDialog
      open={offer !== null}
      onOpenChange={onOpenChange}
      size="sm"
      title="Zrušit slevový termín?"
      description="Tuto akci nelze vzít zpět."
      loading={isCancelling}
      footer={
        <>
          <LGoButton variant="secondary" onClick={() => onOpenChange(false)}>
            Zpět
          </LGoButton>
          <LGoButton
            variant="destructive"
            onClick={onConfirm}
            loading={isCancelling}
            disabled={preview.isLoading}
          >
            Zrušit slevový termín
          </LGoButton>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground">{offer?.activityName}</p>
        {preview.isLoading && (
          <p className="text-sm text-muted-foreground">Zjišťujeme, koho se to týká…</p>
        )}
        {preview.error && (
          <p className="text-sm text-destructive">
            Nepodařilo se zjistit, koho se zrušení týká. Zkuste to prosím znovu.
          </p>
        )}
        {tally && <CancellationSummary tally={tally} />}
      </div>
    </LGoDialog>
  );
}
